import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { renderMaskedString, shouldShowField } from '../../../../components/InputField/helper';
import { WrapperField } from '../../../../components/InputField/WrapperField';
import { DialogContext } from '../../../../store/context/DialogContext';
import { useTranslation } from '../../../../store/context/TranslationContext';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import InputLabel from '../../../../components/InputField/InputLabel';
import { IPLUS_WIZARD } from '../IPlushelper';
import { buildUserDetailsFieldOrder, validateUserDetailsField } from '../components/IndividualLite/helper';

export const LabelValue = ({ label = "", value = "" }) => (
    <div className='d-flex gap-1 mb-1 text-muted'>
        <div className='fw-semibold fs-6'>{label}:</div>
        <div className='fs-6'>{value}</div>
    </div>
)

const IPlusUserDetailForm = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    hasError,
    setHasError,
    loading,
    setLoading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    isForEDD,
    handleChange,
    onOtpVerify,
}) => {
    const [shouldPerformOtpVerification, setShouldPerformOtpVerification] = useState(false);
    const [stickyErrors, setStickyErrors] = useState({});
    const stickyErrorsRef = useRef({});
    const { showError } = useContext(DialogContext);
    const { t } = useTranslation();

    const memoizedFields = useMemo(() => {
        const taxIdField = iPlusFields?.find(field => field.field === 'taxId');
        const ssnField = iPlusFields?.find(field => field.field === 'ssn');

        return { taxIdField, ssnField };
    }, [iPlusFields]);

    const isTaxIdRendered = useMemo(() => {
        return shouldShowField(data, memoizedFields.taxIdField?.showIf);
    }, [data, memoizedFields.taxIdField]);

    const isSSNRendered = useMemo(() => {
        return shouldShowField(data, memoizedFields.ssnField?.showIf);
    }, [data, memoizedFields.ssnField]);

    const getUserDetailsValidationError = useCallback((jsonField, value, currentData = data) => {
        return validateUserDetailsField(jsonField, value, currentData);
    }, [data]);

    const getWrapperValidationError = useCallback(({
        jsonField,
        value,
        dataObj: currentData,
    }) => getUserDetailsValidationError(jsonField, value, currentData), [getUserDetailsValidationError]);

    const syncStickyErrors = useCallback((updater) => {
        setStickyErrors((prevErrors) => {
            const nextErrors = typeof updater === "function" ? updater(prevErrors) : updater;
            stickyErrorsRef.current = nextErrors;
            return nextErrors;
        });
    }, []);

    useEffect(() => {
        const nextErrors = {};
        iPlusFields.forEach((field) => {
            if (field?.field === 'cyclosUserName' || field?.field === 'Country_Of_Birth') {
                return;
            }
            if (field?.field === 'address.pinCode' && data?.address?.hidePinCode) {
                return;
            }
            if (field?.wizard === wizard && shouldShowField(data, field.showIf)) {
                const value = _.get(data, field.field);
                const error = getUserDetailsValidationError(field, value, data);
                if (error) {
                    _.set(nextErrors, field.field, error);
                } else if (field.field === "mobilePhone" && shouldPerformOtpVerification && !isForEDD) {
                    _.set(nextErrors, field.field, 'Please verify mobile phone with OTP');
                }
            }
        });
        setErrors(_.merge({}, nextErrors, stickyErrorsRef.current));
    }, [data, getUserDetailsValidationError, iPlusFields, isForEDD, setErrors, shouldPerformOtpVerification, stickyErrors, wizard]);

    const checkNext = () => {
        const mergedErrors = _.merge({}, errors, stickyErrorsRef.current);
        const orderedFields = buildUserDetailsFieldOrder({
            includePinCode: !data?.address?.hidePinCode,
            includeUsername: false,
            includeTaxId: isTaxIdRendered,
            includeSSN: isSSNRendered,
            streetBeforeBuilding: true,
        });

        for (const fieldPath of orderedFields) {
            const fieldError = _.get(mergedErrors, fieldPath);
            if (fieldError) {
                setHasError(true);
                showError(fieldError);
                return false;
            }
        }
        syncStickyErrors({});
        setErrors({});
        return true;
    };

    const handleSubmit = () => {
        const isFormFieldValid = checkNext();
        if (isFormFieldValid) {
            setHasError(false);
            return handleUpdate({ nextStep: { wizard: IPLUS_WIZARD.IPlusDeclarationForm } });
        } else {
            return;
        }
    };

    const handleUpdateFormData = (updatedData) => {
        handleChange(updatedData)
    }

    const handleErrors = (field, errorMsg) => {
        syncStickyErrors((prevErrors) => {
            const updatedErrors = _.cloneDeep(prevErrors);
            if (errorMsg) {
                _.set(updatedErrors, field, errorMsg);
            } else {
                _.unset(updatedErrors, field);
            }
            return updatedErrors;
        });
        setErrors((prevErrors) => {
            const updatedErrors = _.cloneDeep(prevErrors);
            if (errorMsg) {
                _.set(updatedErrors, field, errorMsg);
            } else {
                _.unset(updatedErrors, field);
            }
            return updatedErrors;
        })
    }

    return (
        <div className="row">
            {/* <Col className='col-12 d-flex align-items-center justify-content-center'>
                <div className='text-center fw-medium fs-4 py-2 mb-4 fv__form-heading'>{t('Applicant Information')}</div>
            </Col> */}

            <InputLabel label={t("Name")} required />
            <p className='ps-3 ms-0 mb-2' style={{ fontSize: 11, fontStyle: 'italic' }}>
                Please enter your name exactly as it appears on your passport.
            </p>
            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["firstName"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                className={'col-sm-4 mt-0'}
                showError={hasError}
                onError={(errorMsg) => handleErrors('firstName', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["middleName"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                className={'col-sm-4 mt-0'}
                showError={hasError}
                onError={(errorMsg) => handleErrors('middleName', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["lastName"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                className={'col-sm-4 mt-0'}
                showError={hasError}
                onError={(errorMsg) => handleErrors('lastName', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <InputLabel label={t("Address")} required />
            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["address.street"]}
                dataObj={data}
                haveDependentSubFields
                onChange={handleUpdateFormData}
                customProp={iPlusFields?.filter(field => field?.parentField === "address")}
                showError={hasError}
                onError={(errorMsg, streetNameLabel) => {
                    const label = streetNameLabel ? streetNameLabel : "address.street"
                    handleErrors(label, errorMsg)
                }}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["address.buildingNumber"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('address.buildingNumber', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["address.city"]}
                dataObj={data}
                className={'col-12 col-sm-3'}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('address.city', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["address.state"]}
                dataObj={data}
                className={'col-12 col-sm-3'}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('address.state', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            {!data?.address?.hidePinCode && <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["address.pinCode"]}
                dataObj={data}
                className={'col-12 col-sm-3'}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('address.pinCode', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />}

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["address.country"]}
                dataObj={data}
                className={'col-12 col-sm-3 address-country-picker'}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('address.country', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            {data?.address?.hidePinCode && <div className={'col-12 col-sm-3'} aria-hidden="true" />}

            <WrapperField
                disabled
                jsonField={cyclosFields["mobilePhone"]}
                dataObj={data}
                showError={hasError}
                onChange={handleUpdateFormData}
                customOperator={setShouldPerformOtpVerification}
                onError={(errorMsg) => handleErrors('mobilePhone', errorMsg)}
                customProp={onOtpVerify}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                disabled
                jsonField={cyclosFields["email"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                className={'mt-4'}
                onError={(errorMsg) => handleErrors('email', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                jsonField={cyclosFields["gender"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                disabled={asLabel}
                onError={(errorMsg) => handleErrors('gender', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                jsonField={cyclosFields["nationality"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                disabled={asLabel}
                onError={(errorMsg) => handleErrors('nationality', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                jsonField={cyclosFields["dob"]}
                dataObj={data}
                disabled
                className="col-sm-12 col-12"
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('dob', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                jsonField={cyclosFields["taxId"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                disabled={asLabel}
                onError={(errorMsg) => handleErrors('taxId', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            <WrapperField
                jsonField={cyclosFields["ssn"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                disabled={asLabel}
                onError={(errorMsg) => {
                    handleErrors('ssn', errorMsg)
                }}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            {!isSSNRendered && data?.diffMap?.ssn ? <LabelValue label='Previous SSN' value={renderMaskedString(data?.diffMap?.ssn, 'XXX-XX-XXXX')} /> : ""}
            {!isTaxIdRendered && data?.diffMap?.taxId ? <LabelValue label='Previous Passport' value={data?.diffMap?.taxId} /> : ""}

            <WrapperField
                jsonField={cyclosFields["Mother_Maiden_Name"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                disabled={asLabel}
                onError={(errorMsg) => handleErrors('Mother_Maiden_Name', errorMsg)}
                isForEDD={isForEDD}
                customValidation={getWrapperValidationError}
            />

            {!hideButtons && (
                <div className='mt-4'>
                    <ButtonGroupNew
                        onNextClick={handleSubmit}
                        step={1}
                        showPageNum={false}
                        pageNum={1}
                        hideBackButton
                        asLabel={false}
                        loading={loading}
                    />
                </div>
            )}
        </div>
    );
};

export default observer(IPlusUserDetailForm);
